import { FetchResult } from "@apollo/client";
import React, { useCallback } from "react";
import { omit, compact } from "lodash";
import { useForm } from "react-hook-form";
import { useHistory } from "react-router";

import { useQueryParam, NumberParam, withDefault } from "use-query-params";

import FirstStep from "../components/mads/StepForm/FirstStep";
import SecondStep from "../components/mads/StepForm/SecondStep";
import Stepper from "../components/mads/StepForm/Stepper";
import ThirdStep from "../components/mads/StepForm/ThirdStep";
import { EmptyState, Link, ScreenContainer } from "../components/wombat-ui";
import useInsertMad, { MadData } from "../graphql/hooks/mutations/useInsertMad";
import { GET_VIEWER } from "../graphql/hooks/queries/useGetViewer";
import { InsertMadMutationVariables } from "../graphql/types";
import useAddNotification from "../hooks/useAddNotification";
import useTranslations from "../hooks/useTranslations";
import useViewer from "../hooks/useViewer";
import { GET_MADS } from "../graphql/hooks/queries/useGetMads";

export type FormStepProps = {
  onNext: () => void;
  register: ReturnType<typeof useForm>["register"];
  unregister: ReturnType<typeof useForm>["unregister"];
  setValue: ReturnType<typeof useForm>["setValue"];
  getValues: ReturnType<typeof useForm>["getValues"];
  formState: ReturnType<typeof useForm>["formState"];
  errors: ReturnType<typeof useForm>["errors"];
  control: ReturnType<typeof useForm>["control"];
  reset: ReturnType<typeof useForm>["reset"];
};

/**
 * Removing `id` since this is beeing used only for mission creation
 * `talentId` is replaced by `talentIds` so that batch creation of missions is made possible
 */
type MissionFormVariables = Omit<
  InsertMadMutationVariables["input"],
  "talentId" | "id"
> & { talentIds: string[] };

type MissionFormScreenProps = Record<string, never>;

const MissionFormScreen: React.FC<MissionFormScreenProps> = () => {
  const t = useTranslations();
  const {
    id: userId,
    isCapableOfInsertingMads,
    company: { id: companyId, users: companyUsers },
  } = useViewer();

  const [insertMad] = useInsertMad({
    refetchQueries: [{ query: GET_MADS }, { query: GET_VIEWER }],
    awaitRefetchQueries: true,
  });
  const [addNotification] = useAddNotification();

  const {
    register,
    unregister,
    setValue,
    getValues,
    handleSubmit,
    control,
    errors,
    trigger,
    formState,
    reset,
  } = useForm<MissionFormVariables>({
    shouldUnregister: false,
    mode: "onBlur",
    defaultValues: {
      madSupervisorsAttributes: [
        { userId, emailNotificationsEnabled: true },
      ],
    }
  });

  const onSubmit = useCallback(
    (data: Partial<MissionFormVariables>) => {
      const _data: Partial<MissionFormVariables> = {
        campaignId: data.campaignId,
        madSupervisorsAttributes: data.madSupervisorsAttributes,
        startDate: (data.startDate as Date).toISOString(),
        endDate: (data.endDate as Date)?.toISOString(),
        title: data.title,
        missionDescription: data.missionDescription,
        schedule: data.schedule,
        templateAttributes: {
          companyId,
          title: data.templateAttributes.title,
          taskTrackingEnabled: data.templateAttributes.taskTrackingEnabled,
          contractTemplateIds:
            data.templateAttributes.contractTemplateIds || [],
          templateSectionsAttributes:
            data.templateAttributes?.templateSectionsAttributes.map(
              (
                { templateQuestionsAttributes: sectionTQA, ...tsa },
                sectionIndex
              ) => ({
                ...tsa,
                order: sectionIndex,
                templateQuestionsAttributes: sectionTQA.map(({ referentIds, ...tqa }, index) => {
                  return {
                    ...omit(tqa, "subject", "defaultValueSchema", "digest"),
                    expectedTimeUnit: "hours",
                    order: index,
                    referentIds: compact(referentIds).join('').match(/.{36}/g) // this is needed because RHF will assume single unchecked checkboxes means 'false'
                  };
                }),
              })
            ) || [],
        },
      };

      if (data.talentIds) {
        const insertions: Promise<
          FetchResult<MadData, Record<string, any>, Record<string, any>>
        >[] = [];
        data.talentIds.forEach((talentId) => {
          insertions.push(
            insertMad({
              variables: {
                input: {
                  ..._data,
                  talentId,
                },
              },
            })
          );
        });

        Promise.all(insertions)
          .then(() => {
            addNotification({
              variables: {
                title: t("mads.notification.success.title") as string,
                body: t("mads.notification.success.body") as string,
                status: "success",
              },
            });

            history.push(`/campaigns/${data.campaignId}`);
          })
          .catch(() => {
            addNotification({
              variables: {
                title: t("mads.notification.error.title") as string,
                body: t("mads.notification.error.body") as string,
                status: "error",
              },
            });
          });
      } else { // Mad without talent
        insertMad({
          variables: {
            input: {
              ..._data,
            },
          },
        })
          .then(() => {
            addNotification({
              variables: {
                title: t("mads.notification.success.title") as string,
                body: t("mads.notification.success.body") as string,
                status: "success",
              },
            });

            history.push(`/campaigns/${data.campaignId}`);
          })
          .catch(() => {
            addNotification({
              variables: {
                title: t("mads.notification.error.title") as string,
                body: t("mads.notification.error.body") as string,
                status: "error",
              },
            });
          });
      }
    },
    [userId, companyId]
  );

  /**
   * Redirect the user on the page he come from on cancel
   */
  const history = useHistory();

  /**
   * Utility callback to switch steps
   */
  const [activeStep, setActiveStep] = useQueryParam(
    "activeStep",
    withDefault(NumberParam, 0)
  );
  const handleStep = (activeStep) =>
    useCallback(() => {
      setActiveStep(activeStep);
    }, []);
  const handleStepOne = handleStep(0);
  const handleStepTwo = handleStep(1);
  const handleStepThree = handleStep(2);

  return (
    <ScreenContainer
      className="flex"
      error={
        isCapableOfInsertingMads ? undefined : (
          <EmptyState
            title={t("network.unauthorized") as string}
            action={
              <Link to="/" variant="button">
                {t("network.backToHome")}
              </Link>
            }
          />
        )
      }
    >
      <div className="relative flex flex-col flex-grow text-dark-100">
        <form
          className="flex flex-col flex-grow"
          onSubmit={handleSubmit(onSubmit)}
        >
          <div
            className={`${
              activeStep === 0 ? "flex flex-grow flex-col" : "hidden"
            }`}
          >
            <FirstStep
              companyUsers={companyUsers}
              onNext={async () => {
                const result =
                  (await trigger("talentIds")) &&
                  (await trigger("campaignId"));
                if (result) handleStepTwo();
              }}
              {...{
                register,
                unregister,
                setValue,
                getValues,
                control,
                errors,
                formState,
                reset,
              }}
            />
          </div>
          <div
            className={`${
              activeStep === 1 ? "flex flex-grow flex-col" : "hidden"
            }`}
          >
            <SecondStep
              onNext={async () => {
                const result = await trigger();
                if (result) handleStepThree();
              }}
              {...{
                register,
                unregister,
                setValue,
                getValues,
                control,
                errors,
                formState,
                reset,
              }}
            />
          </div>
          <div
            className={`${
              activeStep === 2 ? "flex flex-grow flex-col" : "hidden"
            }`}
          >
            <ThirdStep
              {...{
                register,
                unregister,
                setValue,
                getValues,
                control,
                errors,
                formState,
                reset,
              }}
            />
          </div>
        </form>
        <Stepper
          activeStepIndex={activeStep}
          steps={[
            {
              description: t("mads.whoPartakes") as string,
              onClick: handleStepOne,
            },
            {
              description: t("mads.content") as string,
              onClick: handleStepTwo,
            },
            {
              description: t("mads.launch") as string,
              onClick: handleStepThree,
            },
          ]}
        />
      </div>
    </ScreenContainer>
  );
};

export default MissionFormScreen;
